import React, { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Bar, Radar, Pie, Line, Doughnut, Bubble } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
);

/* =========================================================================
   DESIGN TOKENS — "Score Report" system
   Restrained ink/paper base with a single confident accent (signal blue),
   mono type for every number so the report reads like a certified scorecard
   rather than a colourful consumer dashboard.
   ========================================================================= */
const THEME = {
  ink: "#12131A",
  inkSoft: "#6B7080",
  paper: "#F6F6F3",
  surface: "#FFFFFF",
  line: "#E4E4DD",

  accent: "#2453FF",
  accentSoft: "#EAEEFF",

  good: "#1A7F4B",
  goodSoft: "#E6F5EC",
  bad: "#C4342B",
  badSoft: "#FBEAE8",
  warn: "#B4740E",
  warnSoft: "#FBF0DC",
};

const FONT_DISPLAY = "'Sora','Segoe UI',sans-serif";
const FONT_BODY = "'Inter','Segoe UI',sans-serif";
const FONT_MONO = "'IBM Plex Mono','Consolas',monospace";

const CHART_PALETTE = [
  "#2453FF",
  "#7C86A6",
  "#1A7F4B",
  "#C4342B",
  "#B4740E",
  "#0E8FA6",
  "#7A4FD1",
  "#C24E8E",
];

export default function StudentReport() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [report, setReport] = useState(null);
  const [screenShots, setScreenShots] = useState([]);
  const [cameraShots, setCameraShots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [elimination, setElimination] = useState(null);

  const [testDate, setTestDate] = useState("");


 const tname = localStorage.getItem("reportTestName");

const isInvalidTest =
  tname?.includes("CUSTOM") ||
  tname?.includes("DAILY_CHALLENGE") ||
  tname?.includes("REASONING");

const hideEvidence =
  tname?.includes("CUSTOM") ||
  tname?.includes("DAILY_CHALLENGE") ||
  tname?.includes("REASONING");


  // 🔥 FIRST define function
  const formatTestDate = (testName) => {
    if (!testName) return "No Date";

    const parts = testName.split("_");

    // ✅ auto detect date (best way)
    const dateStr = parts.find(p => p.length === 8);

    if (!dateStr) return "Invalid Date";

    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);

    const dateObj = new Date(`${year}-${month}-${day}`);

    return dateObj.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // 🔥 THEN use it
  useEffect(() => {
    const tname = localStorage.getItem("reportTestName");
    setTestDate(formatTestDate(tname));
  }, []);




  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const uid = localStorage.getItem("userId");

    if (!uid || uid === "undefined" || uid === "null") {
      console.warn("⚠ Invalid userId");
      navigate("/front");
      return;
    }

    setUserId(uid);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tname = localStorage.getItem("reportTestName");
        const uid = localStorage.getItem("userId");

        // 🔥 REPORT API
        const reportRes = await axiosClient.get("/auth/reportTest", {
          params: { testName: tname },
        });

        setReport(reportRes.data.data);

        // 🔥 EVIDENCE API (SAFE)
        // ❌ INVALID TEST → API CALL NA HO
        if (isInvalidTest) {
          console.log("🚫 Skipping Evidence API:", tname);
          setScreenShots([]);
          setCameraShots([]);
          setElimination(null);
        } else {
          // ✅ ONLY NORMAL TEST → API CALL
          try {
            const evidenceRes = await axiosClient.get("/auth/getEvidence", {
              params: { testName: tname, userId: uid },
            });

            const data = evidenceRes.data || {};

            setScreenShots(data.screenCapture || []);
            setCameraShots(data.cameraCapture || []);
            setElimination(data.elimination || null);
          } catch (err) {
            console.log("⚠️ No evidence available");
            setScreenShots([]);
            setCameraShots([]);
          }
        }
      } catch (err) {
        console.error("❌ Report fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);




  // ----------pdf downlaod-----------
  const downloadPDF = async () => {
    try {
      const testName = localStorage.getItem("reportTestName");

      const res = await axiosClient.get("/auth/download-report", {
        params: {
          testName: testName,
        },
        responseType: "blob", // 🔥 VERY IMPORTANT
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", "Interview_Report.pdf");

      document.body.appendChild(link);

      link.click();

      link.remove();
    } catch (error) {
      console.error("PDF Download Error", error);
    }
  };

  const format = (val) => Number(val || 0).toFixed(2);
  // ----------pdf downlaod-----------

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: THEME.paper,
          fontFamily: FONT_BODY,
        }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');
          @keyframes rvSweep { 0% { margin-left: -40%; } 100% { margin-left: 100%; } }
        `}</style>
        <div
          style={{
            background: THEME.surface,
            padding: "36px 44px",
            borderRadius: "4px",
            border: `1px solid ${THEME.line}`,
            boxShadow: "0 24px 48px -28px rgba(18,19,26,0.25)",
            textAlign: "center",
            minWidth: "280px",
          }}
        >
          <img
            src="/Zesty Hii1.png"
            alt="Zesty"
            style={{ width: "48px", marginBottom: "16px" }}
          />

          <h3
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: "17px",
              fontWeight: "700",
              marginBottom: "6px",
              color: THEME.ink,
              letterSpacing: "-.2px",
            }}
          >
            Compiling your report
          </h3>

          <p style={{ fontFamily: FONT_MONO, fontSize: "12px", color: THEME.inkSoft }}>
            fetching results & scoring…
          </p>

          <div
            style={{
              marginTop: "18px",
              height: "3px",
              width: "100%",
              background: THEME.line,
              borderRadius: "999px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: "40%",
                background: THEME.accent,
                animation: "rvSweep 1.2s infinite",
              }}
            />
          </div>
        </div>
      </div>
    );

  const {
    interviewReadinessPercent = 0,
    verdict = "",
    behaviorAnalysis = {},
    algorithmDecision = {},
    logicPerformance = {},
    topicPerformance = [],
    questionBreakdown = [],
  } = report || {};

  const topicLabels = topicPerformance.map((t) => t.topic);
  const topicAccuracy = topicPerformance.map((t) => t.accuracy);
  const topicTime = topicPerformance.map((t) => (t.timeTakenSeconds || 0) / 60);

  const behaviorData = {
    labels: [
      "Focus",
      "Distraction",
      "Consistency",
      "Logic Error",
      "Syntax Error",
      "Optimization",
      "Decision",
      "Logic Gap",
    ],
    datasets: [
      {
        data: [
          format(behaviorAnalysis.focusScore),
          format(behaviorAnalysis.distractionScore),
          format(behaviorAnalysis.consistencyScore),
          format(behaviorAnalysis.logicErrorPercent),
          format(behaviorAnalysis.syntaxErrorPercent),
          format(behaviorAnalysis.optimizationScore),
          format(behaviorAnalysis.decisionMakingScore),
          format(behaviorAnalysis.logicGapPercent),
        ],
        backgroundColor: CHART_PALETTE,
        borderWidth: 0,
      },
    ],
  };

  const formatLabel = (key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/Percent|Score/g, "")
      .trim();

  const getColor = (key) => {
    if (key.includes("focus")) return THEME.good;
    if (key.includes("distraction")) return THEME.bad;
    if (key.includes("consistency")) return THEME.accent;
    if (key.includes("error")) return THEME.warn;
    if (key.includes("optimization")) return "#0E8FA6";
    if (key.includes("decision")) return "#7A4FD1";
    if (key.includes("logicGap")) return "#C24E8E";
    return THEME.inkSoft;
  };

  const algoBarData = {
    labels: ["Easy", "Medium", "Hard"],
    datasets: [
      {
        label: "Difficulty %",
        data: [
          algorithmDecision.easyPercent || 0,
          algorithmDecision.mediumPercent || 0,
          algorithmDecision.hardPercent || 0,
        ],
        backgroundColor: [THEME.good, THEME.warn, THEME.bad],
        borderRadius: 4,
      },
    ],
  };

  const logicData = {
    labels: ["Best Logic", "Your Logic"],
    datasets: [
      {
        data: [
          logicPerformance.bestLogicAverage,
          logicPerformance.userLogicAverage,
        ],
        backgroundColor: [THEME.good, THEME.accent],

        // 🔥 ADD THIS
        borderRadius: {
          topLeft: 4,
          topRight: 4,
        },
        borderSkipped: "bottom", // 👈 only top rounded
      },
    ],
  };

  const logicOptions = {
    responsive: true,
    maintainAspectRatio: false,

    scales: {
      x: {
        grid: {
          display: false, // ❌ remove vertical lines
        },
        border: {
          display: true,
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: false, // 🔥 MAIN: horizontal lines remove
        },
        border: {
          display: true,
        },
      },
    },

    plugins: {
      legend: {
        display: false,
      },
    },

    datasets: {
      bar: {
        barThickness: 35,
        maxBarThickness: 30,
        categoryPercentage: 0.4,
        barPercentage: 0.5,
      },
    },
  };

  const topicChart = {
    labels: topicLabels,
    datasets: [
      {
        label: "Accuracy",
        data: topicAccuracy,
        backgroundColor: THEME.accent,

        borderRadius: {
          topLeft: 4,
          topRight: 4,
        },
        borderSkipped: "bottom",
      },
      {
        label: "Time",
        data: topicTime,
        backgroundColor: "#7C86A6",

        borderRadius: {
          topLeft: 4,
          topRight: 4,
        },
        borderSkipped: "bottom",
      },
    ],
  };

  const lineData = {
    labels: topicLabels,
    datasets: [
      {
        label: "Accuracy %",
        data: topicAccuracy,
        borderColor: THEME.accent,
        tension: 0.3,
      },
      {
        label: "Time (min)",
        data: topicTime,
        borderColor: THEME.warn,
        tension: 0.3,
      },
    ],
  };



  const showTopValues = {
    id: "showTopValues",
    afterDatasetsDraw(chart) {
      const { ctx } = chart;

      chart.data.datasets.forEach((dataset, i) => {
        const meta = chart.getDatasetMeta(i);

        meta.data.forEach((bar, index) => {
          let value = dataset.data[index];
          if (!value && value !== 0) return;

          let label = value;

          // 🔥 CONDITION BASED FORMAT
          if (dataset.label?.toLowerCase().includes("accuracy")) {
            label = `${Number(value).toFixed(1)}%`; // % show
          } else if (dataset.label?.toLowerCase().includes("time")) {
            label = `${Number(value).toFixed(1)} min`; // time show
          } else if (dataset.label?.toLowerCase().includes("difficulty")) {
            label = `${Number(value).toFixed(1)}%`;
          } else {
            label = Number(value).toFixed(1);
          }

          ctx.save();
          ctx.fillStyle = THEME.ink;
          ctx.font = `600 11px ${FONT_MONO}`;
          ctx.textAlign = "center";

          ctx.fillText(label, bar.x, bar.y - 8);

          ctx.restore();
        });
      });
    },
  };

  const pieSliceLabels = {
    id: "pieSliceLabels",
    afterDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const data = chart.data.datasets[0].data;

      meta.data.forEach((arc, index) => {
        const value = Number(data[index]);
        if (!value) return;

        const label = `${value}%`;

        // 🔥 center position of slice
        const angle = (arc.startAngle + arc.endAngle) / 2;
        const radius = (arc.innerRadius + arc.outerRadius) / 2;

        const x = arc.x + Math.cos(angle) * radius;
        const y = arc.y + Math.sin(angle) * radius;

        ctx.save();
        ctx.fillStyle = "#fff";
        ctx.font = `700 12px ${FONT_MONO}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.fillText(label, x, y);

        ctx.restore();
      });
    },
  };

  const doughnutSliceLabels = {
    id: "doughnutSliceLabels",
    afterDraw(chart) {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const data = chart.data.datasets[0].data;

      meta.data.forEach((arc, index) => {
        const value = Number(data[index]);
        if (!value) return;

        // 🔥 center angle
        const angle = (arc.startAngle + arc.endAngle) / 2;

        // 🔥 PERFECT POSITION (andar hi rahe)
        const radius = (arc.innerRadius + arc.outerRadius) / 2;

        const x = arc.x + Math.cos(angle) * radius;
        const y = arc.y + Math.sin(angle) * radius;

        ctx.save();

        ctx.fillStyle = THEME.ink;
        ctx.font = `700 12px ${FONT_MONO}`; // 🔥 bigger text
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        ctx.fillText(`${value}%`, x, y);

        ctx.restore();
      });
    },
  };

  const statusCount = { CORRECT: 0, WRONG: 0 };
  const difficultyCount = { EASY: 0, MEDIUM: 0, HARD: 0 };
  const typeCount = { MCQ: 0, CODING: 0 };

  questionBreakdown.forEach((q) => {
    statusCount[q.status] = (statusCount[q.status] || 0) + 1;
    difficultyCount[q.difficulty] = (difficultyCount[q.difficulty] || 0) + 1;
    typeCount[q.type] = (typeCount[q.type] || 0) + 1;
  });

  const total = statusCount.CORRECT + statusCount.WRONG;

  const correctPercent = total
    ? ((statusCount.CORRECT / total) * 100).toFixed(1)
    : 0;

  const wrongPercent = total
    ? ((statusCount.WRONG / total) * 100).toFixed(1)
    : 0;
  // Status
  const statusData = {
    labels: ["Correct", "Wrong"],
    datasets: [
      {
        data: [correctPercent, wrongPercent], // ✅ percentage use
        backgroundColor: [THEME.good, THEME.inkSoft],
      },
    ],
  };

  // Difficulty
  const difficultyAccuracy = { EASY: [], MEDIUM: [], HARD: [] };

  questionBreakdown.forEach((q) => {
    difficultyAccuracy[q.difficulty]?.push(q.accuracy || 0);
  });

  const avg = (arr) =>
    arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;

  const difficultyChartData = {
    labels: ["Easy", "Medium", "Hard"],
    datasets: [
      {
        label: "Accuracy %",
        data: [
          avg(difficultyAccuracy.EASY),
          avg(difficultyAccuracy.MEDIUM),
          avg(difficultyAccuracy.HARD),
        ],
        backgroundColor: THEME.accent,
        borderRadius: 4,
      },
      {
        label: "Questions",
        data: [
          difficultyCount.EASY,
          difficultyCount.MEDIUM,
          difficultyCount.HARD,
        ],
        backgroundColor: "#C7CCE0",
        borderRadius: 4,
      },
    ],
  };
  // Type

  const mcqStatus = { CORRECT: 0, WRONG: 0 };
  const codingStatus = { CORRECT: 0, WRONG: 0 };

  questionBreakdown.forEach((q) => {
    if (q.type === "MCQ") {
      mcqStatus[q.status] = (mcqStatus[q.status] || 0) + 1;
    }
    if (q.type === "CODING") {
      codingStatus[q.status] = (codingStatus[q.status] || 0) + 1;
    }
  });

  const typeData = {
    labels: ["MCQ", "Coding"],
    datasets: [
      {
        data: [mcqStatus.CORRECT, codingStatus.CORRECT],
        backgroundColor: [THEME.accent, THEME.good],
      },
    ],
  };

  /* ---------------------------------------------------------------------
     Layout tokens
     --------------------------------------------------------------------- */
  const page = {
    background: THEME.paper,
    minHeight: "100vh",
    padding: isMobile ? "16px" : "36px",
    fontFamily: FONT_BODY,
    color: THEME.ink,
  };

  const container = {
    maxWidth: "1320px",
    margin: "auto",
    width: "100%",
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)",
    gap: isMobile ? "16px" : "20px",
  };

  const card = {
    background: THEME.surface,
    border: `1px solid ${THEME.line}`,
    padding: isMobile ? "18px" : "24px",
    borderRadius: "4px",
    width: "100%",
  };

  const cardTitle = {
    fontFamily: FONT_DISPLAY,
    fontSize: "14px",
    fontWeight: "700",
    color: THEME.ink,
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const noData = {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT_MONO,
    fontSize: "13px",
    fontWeight: "600",
    color: THEME.inkSoft,
    letterSpacing: ".3px",
  };

  const outlineBtn = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    background: THEME.surface,
    color: THEME.ink,
    padding: "10px 18px",
    border: `1.5px solid ${THEME.ink}`,
    fontFamily: FONT_BODY,
    fontWeight: "600",
    fontSize: "13px",
    cursor: "pointer",
    borderRadius: "3px",
  };

  const fillBtn = {
    ...outlineBtn,
    background: THEME.ink,
    color: THEME.paper,
    border: `1.5px solid ${THEME.ink}`,
  };

  // Score ring geometry
  const pct = Math.min(100, Math.max(0, Number(format(interviewReadinessPercent))));
  const ringR = 52;
  const ringC = 2 * Math.PI * ringR;
  const ringDash = (pct / 100) * ringC;

  const sectionIndex = (n) => (
    <span
      style={{
        fontFamily: FONT_MONO,
        fontSize: "11px",
        color: THEME.accent,
        fontWeight: "700",
      }}
    >
      {String(n).padStart(2, "0")}
    </span>
  );

  const pill = (kind) => {
    const map = {
      CORRECT: { bg: THEME.goodSoft, fg: THEME.good },
      WRONG: { bg: THEME.badSoft, fg: THEME.bad },
      EASY: { bg: THEME.goodSoft, fg: THEME.good },
      MEDIUM: { bg: THEME.warnSoft, fg: THEME.warn },
      HARD: { bg: THEME.badSoft, fg: THEME.bad },
      MCQ: { bg: THEME.accentSoft, fg: THEME.accent },
      CODING: { bg: THEME.accentSoft, fg: THEME.accent },
    };
    const c = map[kind] || { bg: "#F1F1EC", fg: THEME.inkSoft };
    return {
      display: "inline-block",
      padding: "3px 10px",
      borderRadius: "999px",
      fontFamily: FONT_MONO,
      fontSize: "11px",
      fontWeight: "600",
      background: c.bg,
      color: c.fg,
    };
  };

  return (
    <div style={page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@500;600&display=swap');
        .rv-row:hover { background: #FAFAF7; }
      `}</style>

      <div style={container}>
        {/* ============ LETTERHEAD ============ */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "flex-end",
            gap: "18px",
            marginBottom: "22px",
            paddingBottom: "22px",
            borderBottom: `1.5px solid ${THEME.ink}`,
          }}
        >
          <div>
            {/* <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "11px",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: THEME.accent,
                fontWeight: "700",
                marginBottom: "10px",
              }}
            >
              Interview Readiness Report
            </div> */}
            <h1
              style={{
                fontFamily: FONT_DISPLAY,
                fontSize: isMobile ? "24px" : "32px",
                fontWeight: "800",
                margin: 0,
                letterSpacing: "-0.5px",
                color: THEME.ink,
              }}
            >
              Student Performance Analytics
            </h1>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: "12px" }}>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "12px",
                color: THEME.inkSoft,
                border: `1px dashed ${THEME.line}`,
                padding: "6px 12px",
                borderRadius: "3px",
              }}
            >
              Attempted on · {testDate}
            </div>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button onClick={downloadPDF} style={fillBtn}>
                ↓ Download PDF
              </button>
              <button onClick={() => navigate("/front")} style={outlineBtn}>
                Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* ============ SCORE BAND ============ */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.4fr",
            gap: isMobile ? "16px" : "20px",
            marginBottom: "36px",
          }}
        >
          {/* USER ID CARD */}
          <div style={{ ...card, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: "11px",
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: THEME.inkSoft,
                marginBottom: "14px",
              }}
            >
              Candidate ID
            </div>
            <div
              style={{
                fontFamily: FONT_MONO,
                fontSize: isMobile ? "16px" : "22px",
                fontWeight: "700",
                color: THEME.ink,
                wordBreak: "break-all",
              }}
            >
              {userId && userId !== "undefined" ? userId : "Guest User"}
            </div>
          </div>

          {/* READINESS SCORE RING */}
          <div style={{ ...card, display: "flex", alignItems: "center", gap: "26px", flexWrap: "wrap" }}>
            <svg width="120" height="120" viewBox="0 0 120 120" style={{ flexShrink: 0 }}>
              <circle cx="60" cy="60" r={ringR} stroke={THEME.line} strokeWidth="10" fill="none" />
              <circle
                cx="60"
                cy="60"
                r={ringR}
                stroke={THEME.accent}
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${ringDash} ${ringC}`}
                transform="rotate(-90 60 60)"
              />
              <text x="60" y="56" textAnchor="middle" fontFamily={FONT_MONO} fontSize="22" fontWeight="700" fill={THEME.ink}>
                {pct}%
              </text>
              <text x="60" y="74" textAnchor="middle" fontFamily={FONT_MONO} fontSize="9" letterSpacing="1" fill={THEME.inkSoft}>
                READY
              </text>
            </svg>

            <div>
              {/* <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "11px",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  color: THEME.inkSoft,
                  marginBottom: "8px",
                }}
              >
                Interview Readiness
              </div> */}
              <div
                style={{
                  display: "inline-block",
                  fontFamily: FONT_BODY,
                  fontWeight: "700",
                  fontSize: "14px",
                  color: THEME.accent,
                  background: THEME.accentSoft,
                  padding: "6px 14px",
                  borderRadius: "3px",
                }}
              >
                {verdict.replaceAll("_", " ")}
              </div>
            </div>
          </div>
        </div>

 {/* ============ QUESTION TABLE ============ */}
        <div style={{ marginTop: 48,marginBottom: 36 }}>
          <div style={{ ...cardTitle, fontSize: "18px", marginBottom: 16 }}>{sectionIndex(1)} Question Breakdown</div>

          <div style={{ background: THEME.surface, border: `1px solid ${THEME.line}`, borderRadius: "4px", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center", fontFamily: FONT_BODY, fontSize: "13px" }}>
              <thead style={{ background: THEME.ink, color: THEME.paper }}>
                <tr>
                  <th style={{ padding: 12, fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>NO</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>TYPE</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>DIFFICULTY</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>STATUS</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>TOPIC</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>ACCURACY</th>
                  <th style={{ fontFamily: FONT_MONO, fontSize: 11, letterSpacing: 1, fontWeight: 600 }}>TIME</th>
                </tr>
              </thead>

              <tbody>
                {questionBreakdown.map((q, i) => (
                  <tr key={i} className="rv-row" style={{ borderBottom: `1px solid ${THEME.line}` }}>
                    <td style={{ padding: 10, fontFamily: FONT_MONO, color: THEME.inkSoft }}>{i + 1}</td>
                    <td><span style={pill(q.type)}>{q.type}</span></td>
                    <td><span style={pill(q.difficulty)}>{q.difficulty}</span></td>
                    <td><span style={pill(q.status)}>{q.status}</span></td>
                    <td>{q.topic}</td>
                    <td style={{ fontFamily: FONT_MONO }}>{format(q.accuracy)}%</td>
                    <td style={{ fontFamily: FONT_MONO }}>{q.timeSpent}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* ============ CHARTS ============ */}
        <div style={grid}>
          <div style={card}>
            <div style={cardTitle}>{sectionIndex(2)} Behaviour Analysis</div>
            <div style={{ height: isMobile ? 380 : 340, position: "relative" }}>
              {!behaviorData?.datasets?.length ||
              !behaviorData?.datasets?.[0]?.data?.length ||
              behaviorData.datasets[0].data.every(
                (val) =>
                  val === 0 ||
                  val === "0" ||
                  val === null ||
                  val === undefined ||
                  Number(val) === 0
              ) ? (
                <div style={noData}>No Data Available</div>
              ) : (
                <Doughnut
                  data={behaviorData}
                  plugins={[doughnutSliceLabels]}
                  options={{
                    maintainAspectRatio: false,
                    cutout: "68%",
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { font: { family: FONT_BODY, size: 11 }, color: THEME.inkSoft, boxWidth: 10 },
                      },
                    },
                    layout: { padding: 10 },
                  }}
                />
              )}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(3)} Algorithm Difficulty</div>

            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                { label: "EASY", value: algorithmDecision.easyPercent, c: THEME.good, bg: THEME.goodSoft },
                { label: "MEDIUM", value: algorithmDecision.mediumPercent, c: THEME.warn, bg: THEME.warnSoft },
                { label: "HARD", value: algorithmDecision.hardPercent, c: THEME.bad, bg: THEME.badSoft },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    flex: 1,
                    minWidth: 90,
                    padding: "10px 12px",
                    background: s.bg,
                    borderRadius: "3px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: 1, color: s.c, fontWeight: 700 }}>
                    {s.label}
                  </div>
                  <div style={{ fontFamily: FONT_MONO, fontSize: 18, fontWeight: 700, color: s.c }}>
                    {format(s.value)}%
                  </div>
                </div>
              ))}
            </div>

            <div style={{ height: 300 }}>
              {algoBarData?.datasets?.[0]?.data?.every((val) => val === 0) ? (
                <div style={noData}>No Data Available</div>
              ) : (
                <Bar
                  data={algoBarData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: THEME.ink,
                        padding: 10,
                        cornerRadius: 3,
                        displayColors: false,
                      },
                    },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 12, weight: "600" } } },
                      y: {
                        beginAtZero: true,
                        max: 100,
                        ticks: { stepSize: 20, color: THEME.inkSoft, font: { family: FONT_MONO, size: 10 }, callback: (v) => v + "%" },
                        grid: { color: THEME.line, drawBorder: false },
                        border: { display: false },
                      },
                    },
                    datasets: {
                      bar: { barThickness: 40, maxBarThickness: 48, categoryPercentage: 0.55, barPercentage: 0.75 },
                    },
                  }}
                  plugins={[showTopValues]}
                />
              )}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(4)} Logic Comparison</div>
            <div style={{ height: 320 }}>
              <Doughnut
                data={{
                  labels: logicData.labels,
                  datasets: [{ ...logicData.datasets[0], backgroundColor: [THEME.good, THEME.accent], borderWidth: 0, hoverOffset: 12 }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: "68%",
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11, weight: "600" }, padding: 16, usePointStyle: true, pointStyle: "circle" },
                    },
                    tooltip: {
                      backgroundColor: THEME.surface,
                      titleColor: THEME.ink,
                      bodyColor: THEME.inkSoft,
                      borderColor: THEME.line,
                      borderWidth: 1,
                      padding: 10,
                      cornerRadius: 4,
                      callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}%` },
                    },
                  },
                  animation: { animateRotate: true, duration: 900 },
                }}
              />
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(5)} Topic Accuracy &amp; Time</div>
            <div style={{ height: 360, position: "relative" }}>
              {topicChart?.datasets?.[0]?.data?.every((val) => val === 0) ? (
                <div style={noData}>No Data Available</div>
              ) : (
                <Bar
                  data={topicChart}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { labels: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } },
                      y: { grid: { color: THEME.line }, ticks: { color: THEME.inkSoft, font: { family: FONT_MONO, size: 10 } } },
                    },
                  }}
                />
              )}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(6)} Performance Trend</div>
            <div style={{ height: 320, position: "relative" }}>
              {lineData?.datasets?.[0]?.data?.every((val) => val === 0) ? (
                <div style={noData}>No Data Available</div>
              ) : (
                <Line
                  data={lineData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: { legend: { labels: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } } },
                    scales: {
                      x: { grid: { display: false }, ticks: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } },
                      y: { grid: { color: THEME.line }, ticks: { color: THEME.inkSoft, font: { family: FONT_MONO, size: 10 } } },
                    },
                  }}
                />
              )}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(7)} Difficulty Distribution</div>
            <div style={{ height: 320 }}>
              <Bar
                data={difficultyChartData}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { display: true, labels: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } } },
                  scales: {
                    x: { grid: { display: false }, border: { display: true, color: THEME.line } },
                    y: { beginAtZero: true, grid: { display: false }, border: { display: true, color: THEME.line }, ticks: { color: THEME.inkSoft, font: { family: FONT_MONO, size: 10 } } },
                  },
                }}
                plugins={[showTopValues]}
              />
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>{sectionIndex(8)} Correct vs Wrong</div>
            <div style={{ height: 240, display: "flex", justifyContent: "center" }}>
              <Pie
                data={statusData}
                plugins={[pieSliceLabels]}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { labels: { color: THEME.inkSoft, font: { family: FONT_BODY, size: 11 } } },
                    tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}%` } },
                  },
                }}
              />
            </div>
          </div>
        </div>

       

        {/* ============ EVIDENCE ============ */}
        {!hideEvidence && (
          <div style={{ marginTop: 56 }}>
            <div style={{ ...cardTitle, fontSize: "18px", marginBottom: 18 }}>{sectionIndex(9)} Proctoring Evidence</div>

            {elimination && (
              <div
                style={{
                  background: THEME.badSoft,
                  color: THEME.bad,
                  padding: "12px 16px",
                  borderRadius: "3px",
                  marginBottom: "20px",
                  fontFamily: FONT_MONO,
                  fontSize: "13px",
                  fontWeight: "600",
                  textAlign: "center",
                  border: `1px dashed ${THEME.bad}`,
                }}
              >
                ⚠ {elimination.replaceAll("_", " ")}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: "18px" }}>
              {screenShots.map((img, i) => (
                <div key={i} style={{ background: THEME.surface, border: `1px solid ${THEME.line}`, borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ background: THEME.accentSoft, color: THEME.accent, fontFamily: FONT_MONO, fontSize: "11px", fontWeight: "700", padding: "8px 12px", letterSpacing: ".5px" }}>
                    SCREEN CAPTURE · #{i + 1}
                  </div>
                  <img src={img} alt="screen" style={{ width: "100%", height: "200px", objectFit: "contain", background: THEME.paper }} />
                </div>
              ))}

              {cameraShots.map((img, i) => (
                <div key={i} style={{ background: THEME.surface, border: `1px solid ${THEME.line}`, borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ background: THEME.badSoft, color: THEME.bad, fontFamily: FONT_MONO, fontSize: "11px", fontWeight: "700", padding: "8px 12px", letterSpacing: ".5px" }}>
                    CAMERA CAPTURE · #{i + 1}
                  </div>
                  <img src={img} alt="camera" style={{ width: "100%", height: "200px", objectFit: "cover" }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}